import { useEffect, useMemo, useState } from "react";
import { SearchBoxVariant } from "~/features/orders/orderList/components/OrderListSearchBox/SearchBoxVariant";
import { resolveApiUrl } from "~/utils/url/urlResolver";
import { useTranslation } from "react-i18next";
import { useDesignToggle } from "~/features/orders/common/components/NewDesignToggle";

enum DROPDOWN_TYPE {
  ORDER_STATUS,
  ORDER_LINE_STAUTS,
  QUOTE_STATUS,
  QUOTE_LINE_STATUS,
}

export const ALL_CODE = "all";

const VARIANT_MAP: Dict<{ document: DROPDOWN_TYPE; lineItem: DROPDOWN_TYPE }> = {
  [SearchBoxVariant.ORDER]: {
    document: DROPDOWN_TYPE.ORDER_STATUS,
    lineItem: DROPDOWN_TYPE.ORDER_LINE_STAUTS,
  },
  [SearchBoxVariant.QUOTE]: {
    document: DROPDOWN_TYPE.QUOTE_STATUS,
    lineItem: DROPDOWN_TYPE.QUOTE_LINE_STATUS,
  },
};

export const useDropdownDataProvider = (variant: SearchBoxVariant) => {
  const { t } = useTranslation();
  const [orderStatusOptions, setOrderStatusOptions] = useState<DropdownOption[]>([]);
  const [lineItemStatusOptions, setLineItemStatusOptions] = useState<DropdownOption[]>([]);
  const [loading, setLoading] = useState(true);
  const { isNewDesignEnabled } = useDesignToggle();

  useEffect(() => {
    let active = true;
    const loadOptions = async () => {
      setLoading(true);
      const [status, lineItemStatus] = await Promise.all([
        getOrderQuotesDropdownsOptions(VARIANT_MAP[variant].document),
        getOrderQuotesDropdownsOptions(VARIANT_MAP[variant].lineItem),
      ]);
      if (!active) {
        return;
      }
      setOrderStatusOptions(status);
      setLineItemStatusOptions(lineItemStatus);
      setLoading(false);
    };

    void loadOptions();
    return () => {
      active = false;
    };
  }, [variant]);

  const transform = (item: { label: string; id: string }) => ({
    label: t(item.label),
    value: item.id,
  });

  const orderStatuses = useMemo(() => {
    const source = isNewDesignEnabled ? filterOrderStatuses(orderStatusOptions, variant) : orderStatusOptions;
    return source.map(transform);
  }, [orderStatusOptions, isNewDesignEnabled, variant]);

  const lineItemStatuses = useMemo(
    () => lineItemStatusOptions.map(transform),
    [lineItemStatusOptions, isNewDesignEnabled],
  );

  return {
    loading,
    orderStatuses,
    lineItemStatuses,
  };
};

const getOrderQuotesDropdownsOptions = (type: DROPDOWN_TYPE): Promise<DropdownOption[]> => {
  let url = "/my-account";

  switch (type) {
    case DROPDOWN_TYPE.ORDER_STATUS:
      url += "/orderOptions";
      break;
    case DROPDOWN_TYPE.ORDER_LINE_STAUTS:
      url += "/orderLineOptions";
      break;
    case DROPDOWN_TYPE.QUOTE_STATUS:
      url += "/quoteOptions";
      break;
    case DROPDOWN_TYPE.QUOTE_LINE_STATUS:
      url += "/quoteLineOptions";
      break;
    default:
      return Promise.reject(new Error("Please provide a DROPDOWN TYPE parameter"));
  }

  url = resolveApiUrl(url);

  return fetch(url, {
    credentials: "same-origin", // include, omit
    mode: "cors", // no-cors, same-origin
    referrer: "no-referrer", // client
    method: "GET",
  })
    .then((data) => data.json())
    .then((data) => data.map((item: DropdownOption) => ({ id: item.id ?? "", label: item.label }) as DropdownOption));
};

interface DropdownOption {
  id: string;
  label: string;
}

const ORDER_STATUS_WHITELIST = new Set<string>([
  "order.status.definition.all",
  "order.status.definition.invoiced",
  "order.status.definition.delivered",
]);

const QUOTE_STATUS_WHITELIST = new Set<string>([
  "quote.status.definition.all",
  "quote.status.definition.active",
  "quote.status.definition.expired",
  "quote.status.definition.expiringSoon",
]);

export const filterOrderStatuses = (options: DropdownOption[], variant: SearchBoxVariant) => {
  if (variant !== SearchBoxVariant.ORDER) {
    return options.filter((opt) => QUOTE_STATUS_WHITELIST.has(opt.label));
  }
  return options.filter((opt) => ORDER_STATUS_WHITELIST.has(opt.label));
};
